/**
 * Step · Verify Phone — mandatory Twilio SMS OTP gate for onboarding v2.
 * If the user's phone is already verified (phone-signup path), we show a
 * green "verified" pill and enable Continue immediately. Otherwise we
 * collect a phone number, send an OTP via Twilio Verify, and require it.
 */
import { useEffect, useState } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { Phone, Loader2, CheckCircle, ArrowRight, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function StepVerifyPhone({ onNext }) {
  const { user, checkAuth } = useAuth();
  const [phase, setPhase] = useState('enter-phone'); // 'enter-phone' | 'enter-code'
  const [phone, setPhone] = useState(user?.phone_number || '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const verified = !!user?.phone_verified;

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const id = setInterval(() => setResendCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCountdown]);

  const sendCode = async () => {
    const p = phone.trim();
    if (!p || p.length < 7) {
      toast.error('Enter a valid phone number');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/phone/send-code', { phone: p });
      toast.success('Code sent — check your texts');
      setPhase('enter-code');
      setResendCountdown(60);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send code");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (code.trim().length !== 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setBusy(true);
    try {
      // /auth/phone/link attaches the phone to the current authenticated
      // account (works for email/Google/Apple users). For phone-signup
      // users, this step is skipped entirely because they're already
      // phone_verified from `/auth/phone/verify-code` during signup.
      await api.post('/auth/phone/link', { phone: phone.trim(), code: code.trim() });
      toast.success('Phone verified ✓');
      await checkAuth?.();
      onNext?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (resendCountdown > 0 || busy) return;
    setBusy(true);
    try {
      await api.post('/auth/phone/send-code', { phone: phone.trim() });
      toast.success('New code sent');
      setResendCountdown(60);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send code');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6" data-testid="step-verify-phone">
      <div>
        <h2 className="text-2xl font-bold mb-1">Verify your phone</h2>
        <p className="text-zinc-500 text-sm">
          We only text you for account safety — never for promos.
        </p>
      </div>

      {verified ? (
        <div className="bg-gradient-to-br from-[#F0FDF4] to-white border border-[#16A34A]/30 rounded-2xl p-5 flex items-center gap-4" data-testid="phone-already-verified">
          <span className="w-12 h-12 rounded-full bg-[#16A34A]/10 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={22} className="text-[#16A34A]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-[#0F0F10]">Already verified</p>
            <p className="text-[12px] text-[#4B4B52] truncate">{user?.phone_number || 'Your phone'}</p>
          </div>
        </div>
      ) : phase === 'enter-phone' ? (
        <div className="bg-gradient-to-br from-[#FEF2F2] to-white border border-[#B91C1C]/20 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-10 rounded-full bg-[#B91C1C]/10 flex items-center justify-center">
              <Phone size={16} className="text-[#B91C1C]" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-[#0F0F10]">Enter your mobile number</p>
              <p className="text-[12px] text-[#4B4B52]">We&apos;ll text a 6-digit code (include country code)</p>
            </div>
          </div>
          <input
            type="tel" inputMode="tel"
            placeholder="+1 555 123 4567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full font-mono text-lg py-3 px-4 rounded-xl bg-white border border-[var(--hairline-strong)] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30"
            data-testid="verify-phone-input"
            autoFocus
          />
        </div>
      ) : (
        <div className="bg-gradient-to-br from-[#FEF2F2] to-white border border-[#B91C1C]/20 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-full bg-[#B91C1C]/10 flex items-center justify-center">
              <Phone size={16} className="text-[#B91C1C]" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-[#0F0F10]">Enter the 6-digit code</p>
              <p className="text-[12px] text-[#4B4B52] truncate">Sent to <span className="font-semibold">{phone}</span></p>
            </div>
            <button
              onClick={() => { setPhase('enter-phone'); setCode(''); }}
              data-testid="verify-phone-edit"
              className="text-[11px] font-semibold text-[#B91C1C] hover:underline"
            >
              Edit
            </button>
          </div>
          <input
            type="text" inputMode="numeric" pattern="[0-9]*"
            maxLength={6} placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full text-center font-mono text-2xl tracking-[0.5em] font-bold py-3 rounded-xl bg-white border border-[var(--hairline-strong)] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30"
            data-testid="verify-phone-code-input"
            autoFocus
          />
          <button
            onClick={resend}
            disabled={busy || resendCountdown > 0}
            data-testid="verify-phone-resend"
            className="mt-3 text-[12px] font-semibold text-[#B91C1C] hover:underline disabled:opacity-50 disabled:no-underline"
          >
            {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Didn't get it? Resend code"}
          </button>
        </div>
      )}

      <button
        onClick={
          verified ? onNext
          : phase === 'enter-phone' ? sendCode
          : verify
        }
        disabled={
          busy ||
          (!verified && phase === 'enter-phone' && phone.trim().length < 7) ||
          (!verified && phase === 'enter-code' && code.length !== 6)
        }
        data-testid="verify-phone-next"
        className="w-full brand-gradient text-white py-3.5 rounded-xl font-semibold transition-all hover:shadow-[0_0_30px_rgba(255,51,120,0.2)] disabled:opacity-30 active:scale-[0.98] inline-flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
        {verified ? 'Continue'
          : phase === 'enter-phone' ? 'Send Code'
          : 'Verify & Continue'}
        {!busy && <ArrowRight size={16} />}
      </button>
    </div>
  );
}
